/**
 * What to call a passkey, when nobody has named it.
 *
 * 🔴 **A DATE IS NOT A NAME** (PNK-149). `Security.vue` passed `new Date().toLocaleDateString()`
 * as the passkey's name, which was wrong twice over:
 *
 *  1. The name's job is to let you tell keys apart well enough to remove the RIGHT one — and
 *     removing the wrong one is the failure that matters (PNK-142 was exactly that, for Google
 *     accounts). A date answers "when", not "which", and two keys enrolled in one day were
 *     indistinguishable.
 *  2. Worse, that string was also sent as WebAuthn's `user.name`, which is the ACCOUNT IDENTIFIER
 *     the operating system displays in its own passkey list. A real enrolment on 2026-09-16 put
 *     `"9/16/2026"` into the person's password manager, with nothing to say it was Pinksy or them
 *     — and we cannot correct it there afterwards. `user.name` is now the e-mail address, which is
 *     what the spec means by it.
 *
 * So the label is DERIVED AT READ TIME from what the authenticator already told us, and nothing
 * extra is stored: the `aaguid` identifies the authenticator model, and `createdAt` is already on
 * the row. Devin's convention (2026-09-16): *"the date plus the site that you're going to"* — here
 * the authenticator is the more useful half of "where", since the site is always Pinksy.
 *
 * 🔴 **A CURATED LIST, NOT A VENDORED REGISTRY.** The full FIDO metadata service has hundreds of
 * entries and changes without us; `@better-auth/passkey` bundles its own copy but does not export
 * it. These are the ones a person on this platform actually uses, and anything else degrades to the
 * date alone rather than to a wrong name.
 */
const AUTHENTICATORS: Readonly<Record<string, string>> = {
  "ea9b8d66-4d01-1d21-3ce4-b6b48cb575d4": "Google Password Manager",
  "fbfc3007-154e-4ecc-8c0b-6e020557d7bd": "Apple Passwords",
  "dd4ec289-e01d-41c9-bb89-70fa845d4bf2": "iCloud Keychain",
  "08987058-cadc-4b81-b6e1-30de50dcbe96": "Windows Hello",
  "9ddd1817-af5a-4672-a2b9-3e3dd95000a9": "Windows Hello",
  "bada5566-a7aa-401f-bd96-45619a55120d": "1Password",
  "d548826e-79b4-db40-a3d8-11116f7e8349": "Bitwarden",
  "adce0002-35bc-c60a-648b-0b25f1f05503": "Chrome on Mac",
};

/** The authenticator's name, or null when it is one we do not have a name for. */
export function authenticatorNameFor(aaguid: string | null): string | null {
  if (aaguid === null) return null;
  const trimmed = aaguid.trim().toLowerCase();
  // An all-zero AAGUID is the spec's "declined to identify itself", not a lookup miss.
  if (trimmed === "" || /^0+(-0+)*$/u.test(trimmed)) return null;
  return AUTHENTICATORS[trimmed] ?? null;
}

/**
 * The label a passkey is shown under: a name the person chose, else the authenticator and the day
 * it was enrolled, else whichever half we have.
 *
 * `locale` is passed rather than read, because `toLocaleDateString()` with no argument is the
 * BROWSER's locale — which is how an `en-ZA` app rendered `9/16/2026`.
 */
export function passkeyLabel(
  key: { name: string | null; aaguid?: string | null; createdAt: string | null },
  locale: string,
): string | null {
  if (key.name !== null && key.name.trim() !== "") return key.name.trim();
  const authenticator = authenticatorNameFor(key.aaguid ?? null);
  const when = key.createdAt === null ? null : dayIn(key.createdAt, locale);
  if (authenticator !== null && when !== null) return `${authenticator} · ${when}`;
  return authenticator ?? when;
}

function dayIn(iso: string, locale: string): string | null {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  return at.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}
